import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Button } from '@understory-io/leaf/button';
import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { TextField } from '@understory-io/leaf/text-field';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFetcher } from 'react-router';
import { css } from '@understory-io/leaf/css';
import { CheckmarkIcon } from '~/ui/icons/checkmark';
import {
  isActionFailure,
  isActionSuccess,
} from '~/lib/react-router/action-result';
import {
  createSubmitSettleDetector,
  type WaitlistSubmitOutcome,
} from './waitlist-submit';

/**
 * Hooks for the consumer's analytics. The form reports what happened and the
 * route decides what to send, so this component stays free of tracking SDKs.
 */
export type WaitlistTrackingProps = {
  /** Called once, when the form first shows. */
  onViewed?: () => void;
  /** Called once per post, with what the action answered. */
  onSubmitted?: (outcome: WaitlistSubmitOutcome) => void;
};

type WaitlistProps = WaitlistTrackingProps & {
  /**
   * Rendered above the email form — e.g. a heading + explanatory text, or an
   * empty-state avatar. Owned by the consumer (each use case supplies its own
   * copy); hidden once the signup succeeds so the confirmation stands alone.
   */
  header?: ReactNode;
  /**
   * Rendered below the submit button — e.g. an "Or request an event" link.
   * Owned by the consumer; hidden once the signup succeeds.
   */
  footer?: ReactNode;
};

/**
 * The waitlist signup form: an email field + "Get notified" submit button that
 * posts `intent: 'join-waitlist'` to the enclosing route's action. It has a
 * single, always-open appearance — the surrounding chrome (heading, subtitle,
 * back arrow, card padding) belongs to the consumer via `header` / `footer`.
 * On a successful signup the whole thing collapses to a confirmation message.
 */
export function Waitlist({
  header,
  footer,
  onViewed,
  onSubmitted,
}: WaitlistProps = {}) {
  const intl = useIntl();
  const fetcher = useFetcher();
  const [email, setEmail] = useState('');

  // Consumers pass inline arrows, so the callbacks live in a ref: as effect
  // dependencies they would re-arm the effects below on every render.
  const callbacks = useRef({ onViewed, onSubmitted });
  useEffect(() => {
    callbacks.current = { onViewed, onSubmitted };
  });

  // A ref rather than relying on the empty dependency list alone, because
  // StrictMode runs mount effects twice.
  const viewed = useRef(false);
  useEffect(() => {
    if (viewed.current) return;
    viewed.current = true;
    callbacks.current.onViewed?.();
  }, []);

  const [observeSettle] = useState(createSubmitSettleDetector);
  useEffect(() => {
    const outcome = observeSettle(fetcher.state, fetcher.data);
    if (outcome) callbacks.current.onSubmitted?.(outcome);
  }, [observeSettle, fetcher.state, fetcher.data]);

  const isSubmitting = fetcher.state !== 'idle';
  const isSuccess =
    isActionSuccess(fetcher.data) && fetcher.data.intent === 'join-waitlist';

  const errorCode = isSubmitting ? null : extractErrorCode(fetcher.data);

  let errorMessage: string | null = null;
  if (errorCode === 'invalid-email') {
    errorMessage = intl.formatMessage({
      defaultMessage: 'Please enter a valid email address.',
      description: 'Waitlist invalid email validation error',
    });
  } else if (errorCode === 'server-error') {
    errorMessage = intl.formatMessage({
      defaultMessage: 'An error occurred. Please try again.',
      description: 'Waitlist generic server error',
    });
  }

  const errorProps = errorMessage
    ? { tone: 'critical' as const, message: errorMessage }
    : {};

  if (isSuccess) {
    return (
      <Stack gap="small" align="center" className={css({ textAlign: 'center' })}>
        <CheckmarkIcon size="large" />
        <Text style="body-small-bold">
          <FormattedMessage
            defaultMessage="Got it!"
            description="Waitlist signup success title"
          />
        </Text>
        <Text
          style="body-small-regular"
          className={css({ color: 'content.secondary' })}
        >
          <FormattedMessage
            defaultMessage="We'll send you an email when new dates are available."
            description="Waitlist signup success message"
          />
        </Text>
      </Stack>
    );
  }

  return (
    <fetcher.Form method="post" className={css({ width: 'full' })}>
      <Stack gap="medium" className={css({ width: 'full' })}>
        {header}
        <Stack gap="small" className={css({ width: 'full' })}>
          <input type="hidden" name="intent" value="join-waitlist" />
          <TextField
            name="email"
            label=""
            aria-label={intl.formatMessage({
              defaultMessage: 'Email address',
              description: 'Waitlist email input aria label',
            })}
            placeholder={intl.formatMessage({
              defaultMessage: 'Enter your email',
              description: 'Waitlist email input placeholder',
            })}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            {...errorProps}
          />
          <Button
            type="submit"
            appearance="secondary"
            size="standard"
            busy={isSubmitting}
            disabled={!email.trim() || isSubmitting}
            className={css({ width: 'full' })}
          >
            <FormattedMessage
              defaultMessage="Get notified"
              description="Waitlist signup submit button"
            />
          </Button>
        </Stack>
        {footer}
      </Stack>
    </fetcher.Form>
  );
}

function extractErrorCode(
  fetcherData: unknown
): 'invalid-email' | 'server-error' | null {
  if (
    isActionFailure(fetcherData) &&
    fetcherData.intent === 'join-waitlist' &&
    'error' in fetcherData
  ) {
    return fetcherData.error === 'invalid-email'
      ? 'invalid-email'
      : 'server-error';
  }
  return null;
}
